import { Capacitor } from "@capacitor/core";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { auth, db, functions, storage } from "../firebase";

const call = (name) => httpsCallable(functions, name);

export const TERMS_VERSIONS = Object.freeze({
  termsVersion: "2026-08-17.2",
  privacyVersion: "2026-08-17.2",
  driverRulesVersion: "2026-08-17.2",
  biometricNoticeVersion: "2026-08-17.2",
});

export async function loadDriverOnboarding(uid = auth.currentUser?.uid) {
  if (!uid) return { draft: null, kyc: null, driver: null };
  const [draftSnap, kycSnap, driverSnap] = await Promise.all([
    getDoc(doc(db, "driver_onboarding", uid)),
    getDoc(doc(db, "driver_kyc", uid)),
    getDoc(doc(db, "drivers", uid)),
  ]);
  return {
    draft: draftSnap.exists() ? draftSnap.data() : null,
    kyc: kycSnap.exists() ? kycSnap.data() : null,
    driver: driverSnap.exists() ? driverSnap.data() : null,
  };
}

export async function saveDriverDraft(draft, currentStep) {
  return (await call("saveDriverOnboardingDraft")({ draft, currentStep })).data;
}

export async function startDiditVerification() {
  return (await call("startDiditDriverVerification")({ platform: Capacitor.getPlatform() })).data;
}

export async function submitDriverOnboarding() {
  return (await call("submitDriverOnboarding")({})).data;
}

export async function getDriverEligibility() {
  return (await call("getDriverEligibility")({})).data;
}

export async function getDriverCityRequirements(cityName, state, category) {
  return (await call("getDriverCityRequirements")({ cityName, state, category })).data;
}

export async function setDriverOnlineSecure(online) {
  return (await call("setDriverOnline")({ online })).data;
}

export async function acceptDriverRideSecure(rideId) {
  return (await call("acceptDriverRide")({ rideId })).data;
}

export async function updateDriverPlanChoice(plan) {
  return (await call("updateDriverPlanChoice")({ plan })).data;
}

function upload(path, file, onProgress) {
  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(ref(storage, path), file, { contentType: file.type });
    task.on("state_changed", (snapshot) => {
      onProgress?.(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
    }, reject, () => resolve(task.snapshot.ref));
  });
}

export async function uploadDriverProfilePhoto(file, onProgress) {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("Faça login antes de enviar a foto.");
  const uploaded = await upload(`profile_photos/${uid}.jpg`, file, onProgress);
  return getDownloadURL(uploaded);
}

export async function uploadDriverVehicleDocument(file, onProgress) {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("Faça login antes de enviar o documento.");
  const extension = file.type === "application/pdf" ? "pdf" : "jpg";
  const path = `driver_private/${uid}/vehicle/crlv.${extension}`;
  await upload(path, file, onProgress);
  return path;
}

export function extractCallableMessage(error, fallback) {
  return error?.details?.message || error?.message || fallback;
}
